import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { styled } from '@linaria/react'
import { from, minimalVars } from '@ovasabi/ui-minimal/tokens'
import {
  FeatureCard,
  FeatureCardSkeleton,
  Money,
  PageHeader,
  RailTileSkeleton,
  Screen,
  SearchField,
  SectionHeading,
  StatusBadge,
} from '../../components/chow'
import { FlameIcon, JollofRiceIllustration } from '../../components/icons'
import { homeSpecial } from './content'
import { useChooseChow, usePageContext } from './ChooseChowContext'
import { useDragScroll } from '../../hooks/useDragScroll'
import { useHotChefs, useHotDishes, useWeeklyFeatured } from './trending'
import { ChowThumb } from './ChowThumb'
import { FoodNavigation } from './FoodNavigation'
import { Link } from 'react-router-dom'
import { PromoDealsBanner } from './PromoDealsBanner'
import { chefPhoto, dishPhoto } from './dishImages'
import { canonicalSlug } from '../../lib/slug'

/**
 * The warm cream behind the membership pitch. Mixed from `warning` onto
 * `bgSurface` because the palette carries no warm tint of its own.
 */
const WARM_CREAM = `color-mix(in srgb, ${minimalVars.color.warning} 10%, ${minimalVars.color.bgSurface})`

const Style = {
  QuickLinks: styled.nav`
    display: flex; flex-wrap: wrap; gap: ${minimalVars.space.sm};
    a {
      min-height: 44px;
      display: inline-flex;
      align-items: center;
      padding: 0 14px;
      color: ${minimalVars.color.textSecondary};
      background: ${minimalVars.color.bgSurface};
      border: 1px solid ${minimalVars.color.borderSubtle};
      border-radius: ${minimalVars.radius.pill};
      font-weight: 800;
      text-decoration: none;
      transition: border-color 160ms ease, color 160ms ease, background-color 160ms ease;
    }
    a:hover,
    a:focus-visible {
      color: ${minimalVars.color.brand};
      border-color: ${minimalVars.color.brand};
      background: ${minimalVars.color.brandSoft};
    }
  `,
  HeroGrid: styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;

    /* 840px matches no named stop. The two-up hero split needs the wider pane. */
    @media (min-width: 840px) {
      grid-template-columns: 1.15fr 1fr;
      gap: 20px;
      align-items: stretch;
    }
  `,
  Rail: styled.div`
    display: flex;
    gap: 20px;
    overflow-x: auto;
    margin-top: ${minimalVars.space["2xs"]};
    padding: ${minimalVars.space["3xs"]} 0;
    scroll-padding-left: 0;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  `,
  Tile: styled.button`
    flex-shrink: 0;
    width: clamp(200px, 65vw, 260px);
    display: flex;
    flex-direction: column;
    border: 1px solid ${minimalVars.color.borderSubtle};
    background: ${minimalVars.color.bgSurface};
    cursor: pointer;
    text-align: left;
    padding: 0;
    overflow: hidden;
    border-radius: ${minimalVars.radius.md};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    outline: none;
    transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease;

    ${from('page')} {
      width: clamp(220px, 22vw, 280px);
    }

    &:hover {
      transform: translateY(-2px);
      border-color: ${minimalVars.color.borderStrong};
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    &:focus-visible {
      outline: 2px solid ${minimalVars.color.brand};
      outline-offset: 2px;
    }
  `,
  ChefImageWrap: styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    background: ${minimalVars.color.bgSurfaceAlt};
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    svg {
      width: 100%;
      height: 100%;
      display: block;
    }
  `,
  TileMeta: styled.div`
    display: grid;
    gap: ${minimalVars.space["2xs"]};
    padding: ${minimalVars.space.sm};
    width: 100%;
    box-sizing: border-box;
  `,
  TileName: styled.span`
    font-weight: ${minimalVars.typography.weightSemibold};
    font-size: ${minimalVars.typography.bodySize};
    color: ${minimalVars.color.textPrimary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
    display: block;
  `,
  TileSubRow: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
  `,
  TileSubtitle: styled.span`
    font-size: ${minimalVars.typography.captionSize};
    color: ${minimalVars.color.textTertiary};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  DishTile: styled.button`
    flex-shrink: 0;
    width: clamp(200px, 65vw, 260px);
    display: flex;
    flex-direction: column;
    border: 1px solid ${minimalVars.color.borderSubtle};
    background: ${minimalVars.color.bgSurface};
    cursor: pointer;
    text-align: left;
    padding: 0;
    overflow: hidden;
    border-radius: ${minimalVars.radius.md};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    outline: none;
    transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease;

    ${from('page')} {
      width: clamp(220px, 22vw, 280px);
    }

    &:hover {
      transform: translateY(-2px);
      border-color: ${minimalVars.color.borderStrong};
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    &:focus-visible {
      outline: 2px solid ${minimalVars.color.brand};
      outline-offset: 2px;
    }
  `,
  DishImage: styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
    background: ${minimalVars.color.bgSurfaceAlt};
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    svg {
      width: 100%;
      height: 100%;
      display: block;
    }
  `,
  DishFoot: styled.div`
    display: grid;
    gap: ${minimalVars.space["2xs"]};
    padding: ${minimalVars.space.sm};
    width: 100%;
    box-sizing: border-box;
  `,
  DishName: styled.span`
    font-weight: 600;
    font-size: ${minimalVars.typography.bodySize};
    color: ${minimalVars.color.textPrimary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  `,
  DishMeta: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
  `,
  DishPrepTime: styled.span`
    font-size: ${minimalVars.typography.captionSize};
    font-weight: 500;
    color: ${minimalVars.color.textTertiary};
    display: inline-flex;
    align-items: center;
    gap: 3px;
  `,
  MealPlanPitch: styled.div`
    display: grid;
    gap: 14px;
    align-items: center;
    padding: 18px;
    border: 1px solid ${minimalVars.color.borderSubtle};
    border-radius: ${minimalVars.radius.lg};
    background: ${WARM_CREAM};

    /* 760px matches no named stop. It sits just under the page stop on purpose. */
    @media (min-width: 760px) {
      grid-template-columns: minmax(0, 1fr) auto;
      padding: 20px 24px;
    }

    h2 {
      margin: 0 0 ${minimalVars.space["2xs"]};
      font-family: ${minimalVars.typography.displayFamily};
      /* 22px matches SectionHeading app-wide, so this card head keeps the shared section scale. */
      font-size: ${minimalVars.typography.h2Size};
      line-height: 1.2;
    }

    p {
      margin: 0;
      color: ${minimalVars.color.textSecondary};
      line-height: 1.5;
    }

    a {
      min-height: 42px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0 16px;
      border-radius: ${minimalVars.radius.pill};
      background: ${minimalVars.color.brand};
      color: ${minimalVars.color.textInverse};
      font-weight: 800;
      text-decoration: none;
      white-space: nowrap;
    }
  `,
  PopularGrid: styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 12px;
  `,
  PopularCard: styled.button`
    display: grid;
    grid-template-columns: 72px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    min-height: 112px;
    padding: 12px;
    border: 1px solid ${minimalVars.color.borderSubtle};
    border-radius: ${minimalVars.radius.md};
    background: ${minimalVars.color.bgSurface};
    color: inherit;
    text-align: left;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    transition: transform 150ms ease, border-color 150ms ease, box-shadow 150ms ease;

    &:hover {
      transform: translateY(-1px);
      border-color: ${minimalVars.color.brand};
      box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
    }
  `,
  PopularThumb: styled.span`
    width: 72px;
    height: 72px;
    border-radius: ${minimalVars.radius.md};
    overflow: hidden;
  `,
  PopularMeta: styled.span`
    min-width: 0;
    display: grid;
    gap: ${minimalVars.space["2xs"]};
  `,
  PopularName: styled.span`
    font-weight: 800;
    color: ${minimalVars.color.textPrimary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  PopularByline: styled.span`
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  PopularFoot: styled.span`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
    font-size: ${minimalVars.typography.captionSize};
    color: ${minimalVars.color.textTertiary};
  `,
}

export const HomePage = () => {
  const navigate = useNavigate()
  const { isChef } = useChooseChow()
  /**
   * The home search is a way into the dish catalogue, not a filter over this
   * page — nothing on the home screen is a list of dishes to narrow. So it
   * carries the term to /dishes, which already owns searching, rather than
   * duplicating that filter here. Submitting empty just opens the catalogue.
   */
  const [search, setSearch] = useState('')
  const submitSearch = (term: string) => {
    const q = term.trim()
    navigate(q ? `/dishes?q=${encodeURIComponent(q)}` : '/dishes')
  }
  const page = usePageContext('/')
  const chefRail = useDragScroll()
  const dishRail = useDragScroll()
  const hotChefs = useHotChefs(4)
  const hotDishes = useHotDishes(5)
  const featured = useWeeklyFeatured()
  const chefOfWeek = featured.chefOfWeek
  const topChefs = hotChefs.records
  const popular = hotDishes.records
  const trendingSuffix = hotDishes.source === 'live' ? 'demand' : 'curators'
  const chefHeading = 'Kitchens worth knowing'
  const dishHeading = `Popular dishes · ${trendingSuffix}`

  return (
    <Screen
      header={
        isChef ? (
          <PageHeader title={page.title} accentDot>
            <SearchField
              placeholder="Search dishes"
              value={search}
              onChange={setSearch}
              onSubmit={submitSearch}
            />
          </PageHeader>
        ) : (
          <PageHeader title="What sounds good today?">
            <SearchField
              placeholder="Search for a dish or a kitchen"
              value={search}
              onChange={setSearch}
              onSubmit={submitSearch}
            />
          </PageHeader>
        )
      }
    >
      <FoodNavigation expanded />
      <Style.MealPlanPitch>
        <div>
          <h2>Plan your meals for the week.</h2>
          <p>Choose your meals now and pay when you check out.</p>
        </div>
        <Link to="/mealplan">Plan meals →</Link>
      </Style.MealPlanPitch>
      {/* Featured Specials Loading Skeleton or Content */}
      <Style.HeroGrid>
        {featured.loading ? (
          <>
            <FeatureCardSkeleton />
            <FeatureCardSkeleton />
          </>
        ) : (
          <>
            <FeatureCard
              variant="special"
              icon={<FlameIcon size="sm" />}
              eyebrow="Popular Meal"
              title={featured.special?.title || homeSpecial.title}
              subtitle={
                featured.special
                  ? `${featured.special.subtitle}${featured.special.rating ? ` · ★ ${featured.special.rating}` : ''}`
                  : homeSpecial.subtitle
              }
              prepTime={featured.special?.prepTime || '20m prep'}
              ctaLabel="Order Now"
              ctaVariant="solid"
              onCta={() => navigate('/dishes')}
              motif={
                featured.special?.image ? (
                  <ChowThumb
                    src={dishPhoto(featured.special.image, featured.special.category, featured.special.title)}
                    category={featured.special.category}
                    name={featured.special.title}
                    alt={featured.special.title}
                  />
                ) : featured.special?.motif ? (
                  <ChowThumb
                    emoji={featured.special.motif}
                    category={featured.special.category}
                    name={featured.special.title}
                  />
                ) : (
                  <JollofRiceIllustration size="100%" />
                )
              }
            />

            <FeatureCard
              variant="special"
              icon={<FlameIcon size="sm" />}
              eyebrow="Chef of the week"
              title={chefOfWeek?.name || 'Chef spotlight'}
              subtitle={chefOfWeek?.subtitle || 'Explore verified kitchens'}
              prepTime="Fresh daily"
              ctaLabel="See dishes"
              ctaVariant="solid"
              onCta={() => navigate(chefOfWeek?.name ? `/dishes?chef=${encodeURIComponent(chefOfWeek.name)}` : '/kitchens')}
              motif={
                <ChowThumb
                  src={chefPhoto(chefOfWeek?.image)}
                  alt={chefOfWeek?.name || 'Chef spotlight'}
                  name={chefOfWeek?.name}
                  category={chefOfWeek?.subtitle}
                  isChef
                />
              }
            />
          </>
        )}
      </Style.HeroGrid>

      {hotDishes.loading ? (
        <div>
          <SectionHeading title="Popular right now" actionLabel="See all" onAction={() => navigate('/dishes')} />
          <Style.PopularGrid>
            <RailTileSkeleton />
            <RailTileSkeleton />
            <RailTileSkeleton />
          </Style.PopularGrid>
        </div>
      ) : popular.length > 0 && (
        <div>
          <SectionHeading title="Popular right now" actionLabel="See all" onAction={() => navigate('/dishes')} />
          <Style.PopularGrid>
            {popular.slice(0, 3).map((dish) => (
              <Style.PopularCard
                key={dish.id || dish.name}
                type="button"
                onClick={() =>
                  navigate(dish.id ? `/dish/${encodeURIComponent(dish.id)}` : `/dishes?q=${encodeURIComponent(dish.name)}`)
                }
              >
                <Style.PopularThumb aria-hidden>
                  <ChowThumb src={dishPhoto(dish.image, dish.category, dish.name)} emoji={dish.glyph} category={dish.category} name={dish.name} alt="" />
                </Style.PopularThumb>
                <Style.PopularMeta>
                  <Style.PopularName>{dish.name}</Style.PopularName>
                  <Style.PopularByline>by {dish.chef}</Style.PopularByline>
                  <Style.PopularFoot>
                    <Money amount={dish.price} tone="brand" size="sm" />
                    <span>{dish.rating ? `★ ${dish.rating.toFixed(1)}` : 'New'}</span>
                  </Style.PopularFoot>
                </Style.PopularMeta>
              </Style.PopularCard>
            ))}
          </Style.PopularGrid>
        </div>
      )}

      <PromoDealsBanner />

      <Style.QuickLinks aria-label="More ways to explore"><Link to="/kitchens">Browse kitchens →</Link><Link to="/dishes">Explore all dishes →</Link><Link to="/inbox">Order messages →</Link></Style.QuickLinks>

      {/* Hot Chefs Rail */}
      {hotChefs.loading ? (
        <div>
          <SectionHeading title={chefHeading} actionLabel="See all" onAction={() => navigate('/kitchens')} />
          <Style.Rail>
            <RailTileSkeleton />
            <RailTileSkeleton />
            <RailTileSkeleton />
          </Style.Rail>
        </div>
      ) : topChefs.length > 0 ? (
        <div>
          <SectionHeading title={chefHeading} actionLabel="See all" onAction={() => navigate('/kitchens')} />
          <Style.Rail {...chefRail}>
            {topChefs.map((chef) => (
              <Style.Tile
                key={chef.name}
                type="button"
                onClick={() => navigate(`/kitchen/${canonicalSlug(chef.name, (chef as { id?: string }).id || '')}`)}
              >
                <Style.ChefImageWrap>
                  <ChowThumb
                    src={chefPhoto(chef.image, chef.category, chef.name)}
                    emoji={chef.glyph}
                    alt={chef.name}
                    name={chef.name}
                    category={chef.category}
                    thumbnailWidth={384}
                    isChef
                  />
                </Style.ChefImageWrap>
                <Style.TileMeta>
                  <Style.TileName title={chef.name}>{chef.name}</Style.TileName>
                  <Style.TileSubRow>
                    <Style.TileSubtitle>{chef.category}</Style.TileSubtitle>
                    <StatusBadge status={chef.status} />
                  </Style.TileSubRow>
                </Style.TileMeta>
              </Style.Tile>
            ))}
          </Style.Rail>
        </div>
      ) : null}

      {/* Hot Dishes Rail */}
      {hotDishes.loading ? (
        <div>
          <SectionHeading title={dishHeading} actionLabel="See all" onAction={() => navigate('/dishes')} />
          <Style.Rail>
            <RailTileSkeleton />
            <RailTileSkeleton />
            <RailTileSkeleton />
          </Style.Rail>
        </div>
      ) : popular.length > 0 ? (
        <div>
          <SectionHeading title={dishHeading} actionLabel="See all" onAction={() => navigate('/dishes')} />
          <Style.Rail {...dishRail}>
            {popular.map((dish) => (
              <Style.DishTile
                key={dish.name}
                type="button"
                onClick={() =>
                  navigate(dish.id ? `/dish/${encodeURIComponent(dish.id)}` : `/dishes?q=${encodeURIComponent(dish.name)}`)
                }
              >
                <Style.DishImage aria-hidden>
                  <ChowThumb src={dishPhoto(dish.image, dish.category, dish.name)} emoji={dish.glyph} category={dish.category} name={dish.name} alt={dish.name} />
                </Style.DishImage>
                <Style.DishFoot>
                  <Style.DishName>{dish.name}</Style.DishName>
                  <Style.DishMeta>
                    <Money amount={dish.price} tone="brand" size="sm" />
                    <Style.DishPrepTime>⏱ 15m</Style.DishPrepTime>
                  </Style.DishMeta>
                </Style.DishFoot>
              </Style.DishTile>
            ))}
          </Style.Rail>
        </div>
      ) : null}
    </Screen>
  )
}
